/**
 * E018-S008-T005: Unified Relation Service
 *
 * Provides a single API for relation operations that works for both
 * InDoc and CrossDoc scenarios. InDoc is treated as a special case
 * of CrossDoc where sourceDocId === targetDocId.
 *
 * This eliminates the need for consumers to have if/else branches
 * for InDoc vs CrossDoc cases.
 *
 * IMPORTANT: This service is FRONTEND ONLY!
 * The Backend has its own RelationWriteService (sl-tah4) that handles
 * bidirectional sync, steal logic, and cross-doc relations server-side.
 * The MCP/AI tools path (AiDatabaseAdapter) routes relation writes
 * through RelationWriteService automatically.
 */

import { createDatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import type { Store } from '@blocksuite/store';

import {
  createInverseRelation,
  deleteRelationProperty,
  updateInverseColumnCardinality,
  updateInverseRelationCardinality,
} from './database-discovery';
import {
  canModifyDoc,
  createCrossDocInverseRelation,
  type CrossDocRelationResult,
  deleteCrossDocInverseRelation,
  isCrossDocRelationServiceInitialized,
  updateCrossDocColumnCardinality,
  updateCrossDocInverseCardinality,
} from './database-discovery-bridge';
import {
  cleanupDeletedRowReferences,
  restoreInverseRelationValue,
  type SyncContext,
  syncInverseRelation,
} from './sync';

// =============================================================================
// Types
// =============================================================================

export interface CreateInverseRelationParams {
  /** Current document's store (for InDoc operations) */
  store: Store;
  /** Source document ID */
  sourceDocId: string;
  /** Source database block ID */
  sourceDatabaseId: string;
  /** Target document ID (same as sourceDocId for InDoc) */
  targetDocId: string;
  /** Target database block ID */
  targetDatabaseId: string;
  /** Name for the backlink column */
  backlinkName: string;
  /** ID of the source relation property */
  sourceRelationId: string;
  /** Cardinality of the inverse relation (the new column being created) */
  inverseCardinality: 'one' | 'many';
  /** Cardinality of the source relation (for symmetric stealing) */
  sourceCardinality: 'one' | 'many';
}

export interface DeleteInverseRelationParams {
  /** Current document's store (for InDoc operations) */
  store: Store;
  /** Current document ID */
  currentDocId: string;
  /** Target document ID where the inverse relation exists */
  targetDocId: string;
  /** Target database block ID */
  targetDatabaseId: string;
  /** ID of the inverse relation property to delete */
  inverseFieldId: string;
}

export interface RelationOperationResult {
  success: boolean;
  inversePropertyId?: string;
  error?: Error;
}

type RelationCellDataSource = ReturnType<typeof createDatabaseBlockDataSource>;

type TruncationPlanItem = {
  sourceRowId: string;
  previousValue: string[];
  nextValue: string[];
  removedTargetIds: string[];
};

export interface UpdateInverseCardinalityParams {
  /** Current document's store (for InDoc operations) */
  store: Store;
  /** Current document ID */
  currentDocId: string;
  /** Target document ID where the inverse relation exists */
  targetDocId: string;
  /** Target database block ID */
  targetDatabaseId: string;
  /** ID of the inverse relation property to update */
  inverseFieldId: string;
  /** New value for inverseCardinality on the target column */
  newInverseCardinality: 'one' | 'many';
}

export interface UpdateColumnCardinalityParams {
  /** Current document's store (for InDoc operations) */
  store: Store;
  /** Current document ID */
  currentDocId: string;
  /** Target document ID where the inverse relation exists */
  targetDocId: string;
  /** Target database block ID */
  targetDatabaseId: string;
  /** ID of the inverse relation property to update */
  inverseFieldId: string;
  /** New value for cardinality on the target column */
  newCardinality: 'one' | 'many';
}

export interface TruncateCellsParams {
  /** Current document's store (for InDoc operations) */
  store: Store;
  /** Current document ID */
  currentDocId: string;
  /** Target document ID where the database is */
  targetDocId: string;
  /** Target database block ID */
  targetDatabaseId: string;
  /** ID of the property to truncate */
  propertyId: string;
  /** Inverse cardinality - 'one' enables stealing-aware truncation */
  inverseCardinality: 'one' | 'many';

  // === Inverse sync parameters (optional - if not provided, no sync) ===
  /** Database ID of the inverse side (where sync should happen) */
  inverseDatabaseId?: string;
  /** Doc ID of the inverse side (for CrossDoc) */
  inverseDocId?: string;
  /** Field ID on the inverse side to update */
  inverseFieldId?: string;
}

// =============================================================================
// Unified API
// =============================================================================

/**
 * Create an inverse (backlink) relation in the target database.
 *
 * This function handles both InDoc and CrossDoc cases automatically:
 * - InDoc (sourceDocId === targetDocId): Direct BlockSuite operation
 * - CrossDoc: Opens target doc, creates relation, closes doc
 *
 * @example
 * ```typescript
 * // Consumer doesn't need to know if it's CrossDoc or not
 * const result = await createInverseRelationUnified({
 *   store,
 *   sourceDocId: currentDocId,
 *   sourceDatabaseId,
 *   targetDocId,  // Can be same as sourceDocId
 *   targetDatabaseId,
 *   backlinkName: 'Related Items',
 *   sourceRelationId: propertyId,
 *   inverseCardinality: 'many',
 * });
 *
 * if (result.success) {
 *   console.log('Created inverse relation:', result.inversePropertyId);
 * }
 * ```
 */
export async function createInverseRelationUnified(
  params: CreateInverseRelationParams
): Promise<RelationOperationResult> {
  const {
    store,
    sourceDocId,
    sourceDatabaseId,
    targetDocId,
    targetDatabaseId,
    backlinkName,
    sourceRelationId,
    inverseCardinality,
    sourceCardinality,
  } = params;

  // Determine if this is CrossDoc
  const isCrossDoc = targetDocId !== sourceDocId;

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    // CrossDoc: Use the CrossDocRelationService
    const result: CrossDocRelationResult = await createCrossDocInverseRelation(
      sourceDocId,
      sourceDatabaseId,
      targetDocId,
      targetDatabaseId,
      backlinkName,
      sourceRelationId,
      inverseCardinality,
      sourceCardinality
    );

    return {
      success: result.success,
      inversePropertyId: result.inversePropertyId,
      error: result.error,
    };
  }

  // InDoc: Direct BlockSuite operation (fast, no I/O)
  // Note: Even for InDoc, we pass sourceDocId for consistency - the inverse
  // column needs to know where to find the source relation for bidirectional delete.
  try {
    const inversePropertyId = createInverseRelation(
      store,
      sourceDatabaseId,
      targetDatabaseId,
      backlinkName,
      sourceRelationId,
      inverseCardinality,
      sourceCardinality,
      sourceDocId // Pass sourceDocId for symmetric delete support
    );

    if (inversePropertyId) {
      return {
        success: true,
        inversePropertyId,
      };
    }

    return {
      success: false,
      error: new Error('Failed to create inverse relation'),
    };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e : new Error(String(e)),
    };
  }
}

/**
 * Delete an inverse (backlink) relation from the target database.
 *
 * This function handles both InDoc and CrossDoc cases automatically.
 *
 * @example
 * ```typescript
 * const result = await deleteInverseRelationUnified({
 *   store,
 *   currentDocId,
 *   targetDocId: data.targetDocId,
 *   targetDatabaseId: data.targetDatabaseId,
 *   inverseFieldId: data.inverseFieldId,
 * });
 * ```
 */
export async function deleteInverseRelationUnified(
  params: DeleteInverseRelationParams
): Promise<RelationOperationResult> {
  const { store, currentDocId, targetDocId, targetDatabaseId, inverseFieldId } =
    params;

  // Determine if this is CrossDoc
  const isCrossDoc = targetDocId !== currentDocId;

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    // CrossDoc: Use the CrossDocRelationService
    const result = await deleteCrossDocInverseRelation(
      targetDocId,
      targetDatabaseId,
      inverseFieldId
    );

    return {
      success: result.success,
      error: result.error,
    };
  }

  // InDoc: Direct BlockSuite operation
  try {
    deleteRelationProperty(store, targetDatabaseId, inverseFieldId);
    return { success: true };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e : new Error(String(e)),
    };
  }
}

/**
 * Check if the current user can modify the target document.
 *
 * For InDoc operations, always returns true.
 * For CrossDoc operations, checks RBAC permissions.
 */
export async function canModifyTargetDoc(
  currentDocId: string,
  targetDocId: string
): Promise<boolean> {
  if (targetDocId === currentDocId) {
    // InDoc: Always can modify
    return true;
  }

  if (!isCrossDocRelationServiceInitialized()) {
    // Service not initialized, assume can't modify
    return false;
  }

  return canModifyDoc(targetDocId);
}

/**
 * Update the inverseCardinality on the inverse relation column.
 *
 * Called when cardinality is changed on the source column to keep them in sync.
 * This ensures symmetric stealing works in both directions.
 *
 * @example
 * ```typescript
 * // When user changes "Link Type" from "One" to "Many"
 * await updateInverseCardinalityUnified({
 *   store,
 *   currentDocId,
 *   targetDocId,
 *   targetDatabaseId,
 *   inverseFieldId,
 *   newInverseCardinality: 'many', // Our cardinality becomes their inverseCardinality
 * });
 * ```
 */
export async function updateInverseCardinalityUnified(
  params: UpdateInverseCardinalityParams
): Promise<RelationOperationResult> {
  const {
    store,
    currentDocId,
    targetDocId,
    targetDatabaseId,
    inverseFieldId,
    newInverseCardinality,
  } = params;

  // Determine if this is CrossDoc
  const isCrossDoc = targetDocId !== currentDocId;

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    // CrossDoc: Use the CrossDocRelationService
    const result = await updateCrossDocInverseCardinality(
      targetDocId,
      targetDatabaseId,
      inverseFieldId,
      newInverseCardinality
    );

    return {
      success: result.success,
      error: result.error,
    };
  }

  // InDoc: Direct BlockSuite operation
  try {
    const success = updateInverseRelationCardinality(
      store,
      targetDatabaseId,
      inverseFieldId,
      newInverseCardinality
    );
    return { success };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e : new Error(String(e)),
    };
  }
}

/**
 * Update the cardinality on the inverse relation column.
 *
 * Called when "Backlink Type" (inverseCardinality) is changed on the source column.
 * The inverse column's cardinality must match the source's inverseCardinality.
 *
 * @example
 * ```typescript
 * // When user changes "Backlink Type" from "One" to "Many"
 * await updateColumnCardinalityUnified({
 *   store,
 *   currentDocId,
 *   targetDocId,
 *   targetDatabaseId,
 *   inverseFieldId,
 *   newCardinality: 'many', // Our inverseCardinality becomes their cardinality
 * });
 * ```
 */
export async function updateColumnCardinalityUnified(
  params: UpdateColumnCardinalityParams
): Promise<RelationOperationResult> {
  const {
    store,
    currentDocId,
    targetDocId,
    targetDatabaseId,
    inverseFieldId,
    newCardinality,
  } = params;

  // Determine if this is CrossDoc
  const isCrossDoc = targetDocId !== currentDocId;

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    // CrossDoc: Use the CrossDocRelationService
    const result = await updateCrossDocColumnCardinality(
      targetDocId,
      targetDatabaseId,
      inverseFieldId,
      newCardinality
    );

    return {
      success: result.success,
      error: result.error,
    };
  }

  // InDoc: Direct BlockSuite operation
  try {
    const success = updateInverseColumnCardinality(
      store,
      targetDatabaseId,
      inverseFieldId,
      newCardinality
    );
    return { success };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e : new Error(String(e)),
    };
  }
}

/**
 * Truncate all cells in a relation column to keep only the first value.
 *
 * Used when changing cardinality from "many" to "one" to avoid data inconsistency.
 * Handles both InDoc and CrossDoc transparently.
 *
 * IMPORTANT: This function now syncs the inverse side for removed values,
 * ensuring bidirectional consistency (DRY - uses same sync logic as processRelationChange).
 */
export async function truncateCellsUnified(
  params: TruncateCellsParams
): Promise<RelationOperationResult> {
  const {
    store,
    currentDocId,
    targetDocId,
    targetDatabaseId,
    propertyId,
    inverseCardinality,
    // Inverse sync params
    inverseDatabaseId,
    inverseDocId,
    inverseFieldId,
  } = params;

  // Determine if this is CrossDoc truncation
  const isCrossDoc = targetDocId !== currentDocId;

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    return {
      success: false,
      error: new Error(
        'Cross-document cardinality truncation is not supported yet. The relation was not changed.'
      ),
    };
  }

  // InDoc: Truncate with inverse sync
  try {
    const truncationPlan = planCellsToOneTruncation(
      store,
      targetDatabaseId,
      propertyId,
      inverseCardinality
    );

    const inverseRollbackTasks: Array<() => Promise<void>> = [];

    try {
      // Sync inverse side for each removed relation (if inverse is configured)
      if (
        inverseFieldId &&
        inverseDatabaseId &&
        truncationPlan.some(item => item.removedTargetIds.length > 0)
      ) {
        console.log(
          `[truncateCellsUnified] Syncing ${truncationPlan.length} truncated relation rows to inverse side`
        );

        for (const { sourceRowId, removedTargetIds } of truncationPlan) {
          for (const targetRowId of removedTargetIds) {
            const syncContext: SyncContext = {
              store,
              sourceDatabaseId: targetDatabaseId, // The DB we're truncating
              sourcePropertyId: propertyId,
              sourceRowId,
              targetDatabaseId: inverseDatabaseId, // The inverse DB
              targetDocId: inverseDocId,
              inverseFieldId,
              inverseCardinality: 'many', // Remove action filters the value out.
            };
            const previousInverseValue = await syncInverseRelation(
              syncContext,
              targetRowId,
              'remove'
            );
            inverseRollbackTasks.push(() =>
              restoreInverseRelationValue(
                syncContext,
                targetRowId,
                previousInverseValue
              )
            );
          }
        }
      }
    } catch (e) {
      await runInverseRollbackTasks(inverseRollbackTasks);
      throw e;
    }

    try {
      applyTruncationPlan(store, targetDatabaseId, propertyId, truncationPlan);
    } catch (e) {
      await runInverseRollbackTasks(inverseRollbackTasks);
      throw e;
    }

    return { success: true };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e : new Error(String(e)),
    };
  }
}

/**
 * Internal helper: build a truncation plan without mutating the source cells.
 * Source cells are only changed after inverse sync succeeds.
 */
function planCellsToOneTruncation(
  store: Store,
  databaseId: string,
  propertyId: string,
  inverseCardinality: 'one' | 'many' = 'many'
): TruncationPlanItem[] {
  const plan: TruncationPlanItem[] = [];
  const dataSource = getRelationCellDataSource(store, databaseId);
  const rowIds = dataSource.getLogicalRowIdsSnapshot();

  // Track which targets are already claimed (for stealing-awareness)
  const claimedTargets = new Set<string>();

  for (const rowId of rowIds) {
    const value = dataSource.cellValueGet(rowId, propertyId);
    if (!Array.isArray(value) || value.length <= 1) {
      // Single value or empty - still track for stealing-awareness
      if (
        Array.isArray(value) &&
        value.length === 1 &&
        inverseCardinality === 'one'
      ) {
        claimedTargets.add(value[0]);
      }
      continue;
    }

    // Multiple values - need to truncate
    let keptValue: string | null = null;
    const removedTargetIds: string[] = [];

    if (inverseCardinality === 'one') {
      // STEALING-AWARE: Find first unclaimed target
      for (const targetId of value) {
        if (!claimedTargets.has(targetId)) {
          keptValue = targetId;
          claimedTargets.add(targetId);
          break;
        }
      }
      // All others are removed
      for (const targetId of value) {
        if (targetId !== keptValue) {
          removedTargetIds.push(targetId);
        }
      }
    } else {
      // Simple truncation: keep first, remove rest
      keptValue = value[0];
      removedTargetIds.push(...value.slice(1));
    }

    plan.push({
      sourceRowId: rowId,
      previousValue: [...value],
      nextValue: keptValue ? [keptValue] : [],
      removedTargetIds,
    });
  }

  return plan;
}

function applyTruncationPlan(
  store: Store,
  databaseId: string,
  propertyId: string,
  plan: TruncationPlanItem[]
): void {
  if (plan.length === 0) {
    return;
  }
  const dataSource = getRelationCellDataSource(store, databaseId);
  for (const { sourceRowId, nextValue } of plan) {
    dataSource.cellValueChange(sourceRowId, propertyId, nextValue);
  }
}

function getRelationCellDataSource(
  store: Store,
  databaseId: string
): RelationCellDataSource {
  const databases = store.getBlocksByFlavour('affine:database');
  const targetDb = databases.find((db: { id: string }) => db.id === databaseId);

  if (!targetDb) {
    throw new Error(`Database not found: ${databaseId}`);
  }

  const model = (targetDb as unknown as { model?: unknown }).model ?? targetDb;
  return createDatabaseBlockDataSource(model as never);
}

async function runInverseRollbackTasks(
  rollbackTasks: Array<() => Promise<void>>
): Promise<void> {
  for (const rollback of rollbackTasks.reverse()) {
    try {
      await rollback();
    } catch (rollbackError) {
      console.error(
        '[truncateCellsUnified] Failed to rollback inverse truncation:',
        rollbackError
      );
    }
  }
}

/**
 * Cleanup orphaned relation references after rows are deleted.
 *
 * When a row is deleted from a database, any relation cells pointing to it
 * become "orphaned" - they reference a non-existent row. This function:
 * 1. Scans all cells in the specified relation column
 * 2. Removes references to deleted rows
 * 3. Syncs the removal to the inverse side (cross-doc aware)
 *
 * @example
 * ```typescript
 * // Call after deleting rows from a database
 * await cleanupOrphanedRelationsUnified({
 *   store,
 *   currentDocId,
 *   sourceDatabaseId,
 *   sourcePropertyId,
 *   targetDocId,
 *   targetDatabaseId,
 *   inverseFieldId,
 *   inverseCardinality: 'many',
 * });
 * ```
 *
 * @returns Number of orphaned references cleaned up
 */
export async function cleanupOrphanedRelationsUnified(params: {
  store: Store;
  currentDocId: string;
  sourceDatabaseId: string;
  sourcePropertyId: string;
  targetDocId: string | undefined;
  targetDatabaseId: string;
  inverseFieldId: string | undefined;
  inverseCardinality: 'one' | 'many';
}): Promise<{ success: boolean; cleanedCount: number; error?: Error }> {
  const {
    store,
    sourceDatabaseId,
    sourcePropertyId,
    targetDocId,
    targetDatabaseId,
    inverseFieldId,
    inverseCardinality,
  } = params;

  try {
    const cleanedCount = await cleanupDeletedRowReferences(
      store,
      sourceDatabaseId,
      sourcePropertyId,
      targetDatabaseId,
      targetDocId,
      inverseFieldId,
      inverseCardinality
    );
    return { success: true, cleanedCount };
  } catch (e) {
    return {
      success: false,
      cleanedCount: 0,
      error: e instanceof Error ? e : new Error(String(e)),
    };
  }
}
